/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useMemo } from 'react'

import type { Header, Row as TableRow } from '@tanstack/react-table'

import type { CompareRow, Row, RowStatus } from './types'

import { AdminTh, type AdminColumns, useAdminTable } from '../adminTable'

const STATUS_CLASS: Record<RowStatus, string> = {
  match: '',
  mismatch: 'bg-error/10',
  shifted: 'bg-warning/15',
  onlyFresh: 'bg-error/10',
  onlyTimescale: 'bg-info/10',
  duplicate: 'bg-warning/15',
}

export const STATUS_LABEL: Record<RowStatus, string> = {
  match: 'match',
  mismatch: 'mismatch',
  shifted: '±1s',
  onlyFresh: 'Fresh only',
  onlyTimescale: 'Timescale only',
  duplicate: 'duplicate',
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Dates in UTC with seconds — a one-second offset is often the finding. */
export const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return '—'
  if (v instanceof Date) {
    return `${String(v.getUTCFullYear())}-${pad(v.getUTCMonth() + 1)}-${pad(v.getUTCDate())} ${pad(
      v.getUTCHours()
    )}:${pad(v.getUTCMinutes())}:${pad(v.getUTCSeconds())}Z`
  }
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(Math.round(v * 1e6) / 1e6)
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v as string | boolean)
}

/** The union of fields both sides return, key fields first. */
export const columnsOf = (rows: CompareRow[], key: string[] = []): string[] => {
  const all = new Set<string>(key)
  for (const r of rows.slice(0, 200)) {
    for (const side of [r.fresh, r.timescale] as (Row | undefined)[]) {
      if (side) Object.keys(side).forEach((k) => all.add(k))
    }
  }
  return [...all]
}

interface Props {
  rows: CompareRow[]
  keyFields?: string[]
  /** Show only the first n rows. */
  limit?: number
}

/** A comparable value: dates as times, objects as their JSON, nothing as undefined (sorts last). */
const sortValue = (v: unknown): string | number | undefined => {
  if (v === null || v === undefined || v === '') return undefined
  if (v instanceof Date) return v.getTime()
  if (typeof v === 'number') return v
  return formatValue(v)
}

const columnId = (side: 'fresh' | 'timescale', field: string) => `${side}:${field}`

const SideTable: React.FC<{
  title: string
  side: 'fresh' | 'timescale'
  headers: Header<CompareRow, unknown>[]
  rows: TableRow<CompareRow>[]
  columns: string[]
}> = ({ title, side, headers, rows, columns }) => (
  <div className="overflow-x-auto">
    <h3 className="mb-1 text-sm font-semibold">{title}</h3>
    <table className="table table-xs">
      <thead>
        <tr>
          {headers.map((header) => (
            <AdminTh key={header.id} header={header} />
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(({ id, original: r }) => {
          const row = r[side]
          return (
            <tr key={id} className={STATUS_CLASS[r.status]}>
              {columns.map((c) => {
                const cls = r.diffFields.includes(c)
                  ? 'font-semibold text-error'
                  : r.gapFields.includes(c)
                    ? 'text-warning'
                    : ''
                return (
                  <td key={c} className={`whitespace-nowrap ${cls}`}>
                    {row ? formatValue(row[c]) : ''}
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  </div>
)

/**
 * The two result sets aligned row for row. Red cells differ; amber cells are a known,
 * ticketed gap; a row tinted with no values on one side exists only on the other.
 */
export const SideBySide: React.FC<Props> = ({ rows, keyFields, limit }) => {
  const columns = useMemo(() => columnsOf(rows, keyFields), [rows, keyFields])
  // One table behind all three, so sorting any column reorders the sides together and
  // they stay aligned row for row. Sorting covers every row; the limit applies after.
  const tableColumns = useMemo<AdminColumns<CompareRow>>(
    () => [
      { id: 'status', header: 'status', accessorFn: (r) => STATUS_LABEL[r.status] },
      ...(['fresh', 'timescale'] as const).flatMap((side) =>
        columns.map((c): AdminColumns<CompareRow>[number] => ({
          id: columnId(side, c),
          header: c,
          accessorFn: (r) => sortValue(r[side]?.[c]),
        }))
      ),
    ],
    [columns]
  )
  const table = useAdminTable(rows, tableColumns)
  const sorted = table.getRowModel().rows
  const shown = useMemo(() => (limit ? sorted.slice(0, limit) : sorted), [sorted, limit])
  if (!rows.length) return <p className="text-sm text-base-content/70">No rows on either side.</p>
  const headers = table.getHeaderGroups()[0].headers
  const sideHeaders = (side: 'fresh' | 'timescale') => headers.filter((h) => h.column.id.startsWith(`${side}:`))
  return (
    <div className="grid gap-4 xl:grid-cols-[auto_1fr_1fr]" data-testid="side-by-side">
      <div>
        <h3 className="mb-1 text-sm font-semibold">Row</h3>
        <table className="table table-xs">
          <thead>
            <tr>
              <AdminTh header={headers[0]} />
            </tr>
          </thead>
          <tbody>
            {shown.map(({ id, original: r }) => (
              <tr key={id} className={STATUS_CLASS[r.status]}>
                <td className="whitespace-nowrap">{STATUS_LABEL[r.status]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SideTable title="Fresh" side="fresh" headers={sideHeaders('fresh')} rows={shown} columns={columns} />
      <SideTable title="Timescale" side="timescale" headers={sideHeaders('timescale')} rows={shown} columns={columns} />
    </div>
  )
}
